import React from 'react';
import { CardElement } from '@stripe/react-stripe-js';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { OrderingOperations, OrderingSelectors, OrderingConstants } from 'polygon-ordering';
import getThemeLookup from '../selectors/getThemeLookup';
import { setCreditCardComplete } from '../slices/creditCardComplete';
import EVENTS from '../constants/events';
import { logEvent } from '../utils/analytics';
import combineStyles from '../utils/combineStyles';
import { CONTAINER_PROPERTIES } from '../utils/theme';
import Text from './Text';
import PaymentMethod from './PaymentMethod';
import '../styles/stripe.css';
import { loadStripe } from '@stripe/stripe-js';
import getPaymentGatewayPublicKey from '../selectors/getPaymentGatewayPublicKey';
import { useTranslation } from 'react-i18next';
import { TYRO_CONSTANTS, useTyro } from '../hooks/TyroProvider';
import ContentLoader from 'react-content-loader';
import { RiLockLine } from 'react-icons/ri';
import SaveCardSwitch from './SavedCardSwitch';
import { Show } from './atoms/Show';
import { RedcatPaymentCardForm } from '../shared/redcatpayments/components/RedcatPaymentCardForm';
import { RedcatPaymentCardFormStyle } from '../shared/redcatpayments/components/RedcatPaymentCardFormStyle';
import { setCheckoutInProgress } from '../slices/checkoutInProgress';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import { useRedcatPayment } from '../shared/redcatpayments/hooks/useRedcatPayment';
import { trackPaymentFailed, useRedcatAnalytics } from '../analytics';

const { updateSelectedPaymentMethods } = OrderingOperations;
const {
  getSelectedPaymentMethods,
  getGatewayType,
  getOrderTotals,
  getLocationId,
  getGatewayMode,
  getMember,
} = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES, PAYMENT_METHOD } = OrderingConstants;

const PaymentMethodCreditCard = () => {
  const p = useAppSelector(getThemeLookup);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const paymentGatewayPublicKey = useAppSelector(getPaymentGatewayPublicKey);
  const gatewayType = useAppSelector(getGatewayType);
  const orderTotals = useAppSelector(getOrderTotals);
  const locationId = useAppSelector(getLocationId);
  const gatewayMode = useAppSelector(getGatewayMode);
  const member = useAppSelector(getMember);
  const apiHandlerReady = useAppSelector((state: any) => state.apiHandlerReady);
  const checkoutInProgress = useAppSelector(state => state.checkoutInProgress);
  const iframeTextColor = p('adyenCardField', ['color']).color as string | undefined;
  const memberRef = member ? { id: Number(member.id), number: member.memberNumber } : undefined;

  // gatewayType is fixed for the lifetime of this component (set once from config before mount),
  // so this conditional hook call always executes in the same order across renders.
  const { setPollingReference, resetPaymentAttempt } =
    gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useRedcatPayment()
      : { setPollingReference: () => {}, resetPaymentAttempt: () => {} };

  const { track } = useRedcatAnalytics();
  const paymentAnalyticsBase = { purchaseType: 'order' as const, memberId: member?.id };

  const alreadySelected = selectedPaymentMethods.some(selectedPaymentMethod => {
    if (selectedPaymentMethod.method === PAYMENT_METHOD.CREDIT_CARD) {
      return true;
    }
    return false;
  });

  // eslint-disable-next-line
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;
  const { payRequestReady, payFormReady, loading } =
    gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  const displayCreditCardForm = !loading && payRequestReady && payFormReady && alreadySelected;

  return (
    <PaymentMethod
      method={PAYMENT_METHOD.CREDIT_CARD}
      right={
        <div>
          <RiLockLine style={{ fontWeight: 'bolder', marginRight: 4 }} size={18} color="#737373" />
          <span>
            <Text
              style={{
                fontSize: 14,
                color: '#737373',
              }}
              value={t('securePayment')}
            />
          </span>
        </div>
      }
      subtitle={
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: -8,
            marginLeft: 45,
          }}
          className={!alreadySelected ? 'my-2' : ''}
        >
          {alreadySelected && (
            <span id="cvc-helper">
              <Text themeKey="cvcHelper">{t('cvcHelper')}</Text>
            </span>
          )}
        </div>
      }
      additionalBehaviour={() => {
        if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) {
          loadStripe(paymentGatewayPublicKey!);
        }
      }}
    >
      <div style={styles.contentContainer}>
        {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && (
          <div style={combineStyles(styles.input, p('input', CONTAINER_PROPERTIES))}>
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    backgroundColor: 'transparent', // workaround for issue with darkmode
                    color: p('stripeElementText', ['color']).color,
                  },
                },
              }}
              onChange={event => dispatch(setCreditCardComplete(event.complete))}
              onFocus={() => {
                if (!alreadySelected) {
                  dispatch(
                    updateSelectedPaymentMethods({
                      paymentMethod: PAYMENT_METHOD.CREDIT_CARD,
                      alreadySelected,
                    }),
                  );
                }
                logEvent(EVENTS.CHANGE_PAYMENT_METHOD, {
                  label: t(`paymentMethod.${PAYMENT_METHOD.CREDIT_CARD}`),
                });
              }}
            />
          </div>
        )}

        {gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && (
          <>
            <div
              id={TYRO_CONSTANTS.CREDIT_CARD_PAY_HTML_ID}
              style={{
                display: displayCreditCardForm ? 'block' : 'none',
                margin: '0 -8px',
              }}
            ></div>

            {alreadySelected && loading && (
              <ContentLoader
                speed={2}
                width="100%"
                height="100%"
                viewBox="0 0 500 240"
                backgroundColor="#f3f3f3"
                foregroundColor="#ecebeb"
              >
                <rect x="8" y="0" rx="3" ry="3" width="484" height="40" />
                <rect x="8" y="70" rx="3" ry="3" width="484" height="40" />
                <rect x="8" y="140" rx="3" ry="3" width="233" height="40" />
                <rect x="257" y="140" rx="3" ry="3" width="233" height="40" />
              </ContentLoader>
            )}
          </>
        )}

        <Show when={gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY && alreadySelected}>
          <RedcatPaymentCardFormStyle>
            <RedcatPaymentCardForm
              amount={Math.round(orderTotals?.discountedMoneyPrice ?? 0)}
              storeId={locationId ?? ''}
              environment={gatewayMode === 'production' ? 'live' : 'test'}
              apiHandlerReady={apiHandlerReady}
              iframeTextColor={iframeTextColor}
              disabled={checkoutInProgress}
              memberRef={memberRef}
              onCompleted={(data, reference) => {
                if (data.resultCode !== 'Authorised') {
                  trackPaymentFailed(track, {
                    ...paymentAnalyticsBase,
                    failureReason: 'redcat_payment_not_authorised',
                  });
                  enqueueErrorSnackbar(t('paymentFailedMessage'));
                  dispatch(setCheckoutInProgress(false));
                  return;
                }
                if (reference) {
                  setPollingReference(reference);
                } else {
                  trackPaymentFailed(track, {
                    ...paymentAnalyticsBase,
                    failureReason: 'session_reference_missing',
                  });
                  dispatch(setCheckoutInProgress(false));
                }
              }}
              onFailed={() => {
                trackPaymentFailed(track, {
                  ...paymentAnalyticsBase,
                  failureReason: 'redcat_payment_failed',
                });
                enqueueErrorSnackbar(t('paymentFailedMessage'));
                dispatch(setCheckoutInProgress(false));
                resetPaymentAttempt();
              }}
              onError={error => {
                trackPaymentFailed(track, {
                  ...paymentAnalyticsBase,
                  failureReason: 'redcat_payment_error',
                });
                enqueueErrorSnackbar(error?.message || t('paymentFailedMessage'));
                dispatch(setCheckoutInProgress(false));
                resetPaymentAttempt();
              }}
            />
          </RedcatPaymentCardFormStyle>
        </Show>
        {alreadySelected &&
          gatewayType !== PAYMENT_GATEWAY_TYPES.REDCAT_PAY &&
          (gatewayType !== PAYMENT_GATEWAY_TYPES.TYRO || displayCreditCardForm) && (
            <SaveCardSwitch />
          )}
      </div>
    </PaymentMethod>
  );
};

const styles = {
  contentContainer: {
    marginTop: 20,
    marginBottom: 20,
  },
  input: {
    borderRadius: 4,
    border: '1px solid #c4c4c4',
    marginBottom: 20,
  },
};

export default PaymentMethodCreditCard;
